/**
 * AvatarUpload — circle crop UI for avatar images.
 *
 * Two input modes:
 * - "Take photo" → opens front camera via capture="user"
 * - "Upload" → standard file picker
 *
 * Crop approach: the image is rendered in a square viewport with the
 * circular mask as a visual guide. The user drags to pan and uses a
 * slider to zoom. On confirm, a canvas crops the exact visible square
 * region and sends the pre-cropped image to the backend — no crop
 * coordinates needed, the backend just resizes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import './AvatarUpload.css';

const PREVIEW_SIZE = 260;

export default function AvatarUpload({ currentUrl, onUploaded, onRemove }) {
  const [mode, setMode] = useState('idle'); // idle | cropping | uploading
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState('');

  // Image natural dimensions
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  // Pan + zoom state (pixels in preview space)
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError("that doesn't look like an image");
      return;
    }
    setError('');
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setMode('cropping');
  }

  function handleImageLoad() {
    const img = imgRef.current;
    if (!img) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setImgSize({ w, h });

    // Initial scale: fit the shorter side to the preview
    const fitScale = PREVIEW_SIZE / Math.min(w, h);
    setScale(fitScale);

    // Center the image
    setOffset({
      x: (PREVIEW_SIZE - w * fitScale) / 2,
      y: (PREVIEW_SIZE - h * fitScale) / 2,
    });
  }

  // Clamp so the image always covers the full preview square
  const clampOffset = useCallback((ox, oy, s) => {
    const { w, h } = imgSize;
    const sw = w * s;
    const sh = h * s;
    return {
      x: Math.min(0, Math.max(PREVIEW_SIZE - sw, ox)),
      y: Math.min(0, Math.max(PREVIEW_SIZE - sh, oy)),
    };
  }, [imgSize]);

  // Drag to pan
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startX: cx, startY: cy, startOx: offset.x, startOy: offset.y };

    function handleMove(ev) {
      ev.preventDefault();
      const d = dragRef.current;
      if (!d) return;
      const mx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const my = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const newOx = d.startOx + (mx - d.startX);
      const newOy = d.startOy + (my - d.startY);
      setOffset(clampOffset(newOx, newOy, scale));
    }

    function handleEnd() {
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    }

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }, [offset, scale, clampOffset]);

  // Zoom slider
  function handleZoom(e) {
    const { w, h } = imgSize;
    if (!w) return;

    const minScale = PREVIEW_SIZE / Math.min(w, h);
    const maxScale = minScale * 4;
    const pct = Number(e.target.value) / 100;
    const newScale = minScale + pct * (maxScale - minScale);

    // Zoom toward center of the preview
    const cx = PREVIEW_SIZE / 2;
    const cy = PREVIEW_SIZE / 2;
    const ratio = newScale / scale;
    const newOx = cx - (cx - offset.x) * ratio;
    const newOy = cy - (cy - offset.y) * ratio;

    setScale(newScale);
    setOffset(clampOffset(newOx, newOy, newScale));
  }

  const zoomValue = (() => {
    const { w, h } = imgSize;
    if (!w) return 0;
    const minScale = PREVIEW_SIZE / Math.min(w, h);
    const maxScale = minScale * 4;
    return Math.round(((scale - minScale) / (maxScale - minScale)) * 100);
  })();

  // Render the visible square to canvas and upload
  async function handleConfirm() {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    setMode('uploading');
    setError('');

    const outputSize = 512;
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');

    // The preview shows the image at position (offset.x, offset.y) scaled by `scale`.
    // To get the source rectangle in natural image coords:
    const sx = -offset.x / scale;
    const sy = -offset.y / scale;
    const sSize = PREVIEW_SIZE / scale;

    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, outputSize, outputSize);

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setError('failed to process image');
        setMode('cropping');
        return;
      }

      const formData = new FormData();
      formData.append('file', blob, 'avatar.jpg');

      try {
        const token = localStorage.getItem('nh_access');
        const res = await fetch('/api/auth/me/avatar', {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.detail || `Upload failed (${res.status})`);
        }

        const data = await res.json();
        onUploaded(data.avatar_url);
        handleCancel();
      } catch (err) {
        setError(err.message || 'upload failed');
        setMode('cropping');
      }
    }, 'image/jpeg', 0.92);
  }

  function handleCancel() {
    if (imageSrc) URL.revokeObjectURL(imageSrc);
    setImageSrc(null);
    setMode('idle');
    setError('');
    setScale(1);
    setOffset({ x: 0, y: 0 });
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  }

  useEffect(() => {
    return () => { if (imageSrc) URL.revokeObjectURL(imageSrc); };
  }, [imageSrc]);

  const hiddenCanvas = <canvas ref={canvasRef} style={{ display: 'none' }} />;

  // ── idle mode ──
  if (mode === 'idle') {
    return (
      <div className="avu-idle">
        {hiddenCanvas}
        <div className="avu-current">
          {currentUrl ? (
            <img src={`${currentUrl}?t=${Date.now()}`} alt="avatar" className="avu-current-img" />
          ) : (
            <div className="avu-current-placeholder">no photo</div>
          )}
        </div>

        <div className="avu-buttons">
          <label className="avu-btn avu-btn-primary">
            upload photo
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} hidden />
          </label>
          <label className="avu-btn avu-btn-secondary">
            take selfie
            <input ref={cameraRef} type="file" accept="image/*" capture="user" onChange={handleFileSelect} hidden />
          </label>
          {currentUrl && (
            <button className="avu-btn avu-btn-danger" onClick={onRemove}>remove</button>
          )}
        </div>

        {error && <div className="avu-error">⚠ {error}</div>}
      </div>
    );
  }

  // ── crop mode ──
  return (
    <div className="avu-crop-container">
      {hiddenCanvas}
      <div className="avu-crop-label">drag to position, zoom to resize</div>

      <div
        className="avu-crop-viewport"
        style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <img
          ref={imgRef}
          src={imageSrc}
          alt=""
          className="avu-crop-img-direct"
          onLoad={handleImageLoad}
          draggable={false}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: '0 0',
          }}
        />
        <svg className="avu-crop-mask" viewBox={`0 0 ${PREVIEW_SIZE} ${PREVIEW_SIZE}`}>
          <defs>
            <mask id="avu-circle-mask">
              <rect width={PREVIEW_SIZE} height={PREVIEW_SIZE} fill="white" />
              <circle cx={PREVIEW_SIZE/2} cy={PREVIEW_SIZE/2} r={PREVIEW_SIZE/2 - 2} fill="black" />
            </mask>
          </defs>
          <rect
            width={PREVIEW_SIZE} height={PREVIEW_SIZE}
            fill="rgba(0,0,0,0.55)"
            mask="url(#avu-circle-mask)"
          />
          <circle
            cx={PREVIEW_SIZE/2} cy={PREVIEW_SIZE/2} r={PREVIEW_SIZE/2 - 2}
            fill="none" stroke="white" strokeWidth="2"
          />
        </svg>
      </div>

      <div className="avu-zoom-row">
        <span className="avu-zoom-label">zoom</span>
        <input
          type="range" min="0" max="100"
          value={zoomValue}
          onChange={handleZoom}
          className="avu-zoom-slider"
        />
      </div>

      {error && <div className="avu-error">⚠ {error}</div>}

      <div className="avu-crop-actions">
        <button
          className="avu-btn avu-btn-primary"
          onClick={handleConfirm}
          disabled={mode === 'uploading'}
        >
          {mode === 'uploading' ? 'uploading…' : 'save avatar'}
        </button>
        <button
          className="avu-btn avu-btn-ghost"
          onClick={handleCancel}
          disabled={mode === 'uploading'}
        >
          cancel
        </button>
      </div>
    </div>
  );
}
